import React, { useState, useEffect } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const AuditActivityFeed = () => {
  const [activities, setActivities] = useState([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Simulate WebSocket connection for real-time updates
    const mockActivities = [
      {
        id: 1,
        type: "vault_access",
        user: "Sarah Johnson",
        action: "accessed Marketing Vault",
        timestamp: new Date(Date.now() - 300000),
        ip: "192.168.1.45",
        device: "Chrome on Windows",
        severity: "info"
      },
      {
        id: 2,
        type: "sharing",
        user: "Michael Chen",
        action: "shared AWS Credentials with External Team",
        timestamp: new Date(Date.now() - 600000),
        ip: "10.0.0.23",
        device: "Firefox on macOS",
        severity: "warning"
      },
      {
        id: 3,
        type: "policy_violation",
        user: "David Rodriguez",
        action: "attempted to export passwords without approval",
        timestamp: new Date(Date.now() - 900000),
        ip: "172.16.0.12",
        device: "Edge on Windows",
        severity: "critical"
      },
      {
        id: 4,
        type: "vault_creation",
        user: "Emily Watson",
        action: "created new vault: HR Confidential",
        timestamp: new Date(Date.now() - 1200000),
        ip: "192.168.1.67",
        device: "Safari on macOS",
        severity: "info"
      },
      {
        id: 5,
        type: "password_rotation",
        user: "System",
        action: "auto-rotated 15 database credentials",
        timestamp: new Date(Date.now() - 1800000),
        ip: "System",
        device: "Automated Task",
        severity: "success"
      },
      {
        id: 6,
        type: "login_attempt",
        user: "Unknown User",
        action: "failed login attempt from suspicious location",
        timestamp: new Date(Date.now() - 2400000),
        ip: "203.0.113.45",
        device: "Chrome on Linux",
        severity: "critical"
      }
    ];

    setTimeout(() => {
      setActivities(mockActivities);
      setIsLoading(false);
    }, 1000);
  }, []);

  const getActivityIcon = (type) => {
    switch (type) {
      case 'vault_access': return 'Eye';
      case 'sharing': return 'Share2';
      case 'policy_violation': return 'AlertTriangle';
      case 'vault_creation': return 'Plus';
      case 'password_rotation': return 'RotateCcw';
      case 'login_attempt': return 'LogIn';
      default: return 'Activity';
    }
  };

  const getSeverityColor = (severity) => {
    switch (severity) {
      case 'critical': return 'text-error';
      case 'warning': return 'text-warning';
      case 'success': return 'text-success';
      default: return 'text-muted-foreground';
    }
  };

  const getSeverityBg = (severity) => {
    switch (severity) {
      case 'critical': return 'bg-error/10';
      case 'warning': return 'bg-warning/10';
      case 'success': return 'bg-success/10';
      default: return 'bg-muted/50';
    }
  };

  const formatTimestamp = (timestamp) => {
    const now = new Date();
    const diff = now - timestamp;
    const minutes = Math.floor(diff / 60000);
    const hours = Math.floor(diff / 3600000);
    
    if (minutes < 60) {
      return `${minutes}m ago`;
    } else if (hours < 24) {
      return `${hours}h ago`;
    } else {
      return timestamp?.toLocaleDateString();
    }
  };

  const handleViewDetails = (activity) => {
    console.log('Viewing activity details:', activity);
  };

  const handleViewAll = () => {
    console.log('Navigating to full audit log...');
  };

  if (isLoading) {
    return (
      <div className="bg-card border border-border rounded-lg p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span className="text-sm text-muted-foreground">Live</span>
          </div>
        </div>
        <div className="space-y-4">
          {[...Array(5)]?.map((_, i) => (
            <div key={i} className="flex items-start space-x-3 animate-pulse">
              <div className="w-8 h-8 bg-muted rounded-full"></div>
              <div className="flex-1 space-y-2">
                <div className="h-4 bg-muted rounded w-3/4"></div>
                <div className="h-3 bg-muted rounded w-1/2"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="bg-card border border-border rounded-lg p-6">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-lg font-semibold text-foreground">Recent Activity</h2>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <div className="w-2 h-2 bg-success rounded-full animate-pulse"></div>
            <span className="text-sm text-muted-foreground">Live</span>
          </div>
          <Button variant="ghost" size="sm" onClick={handleViewAll}>
            View All
          </Button>
        </div>
      </div>
      <div className="space-y-4 max-h-96 overflow-y-auto">
        {activities?.map((activity) => (
          <div
            key={activity?.id}
            className="flex items-start space-x-3 p-3 rounded-lg hover:bg-muted/50 transition-smooth cursor-pointer"
            onClick={() => handleViewDetails(activity)}
          >
            <div className={`p-2 rounded-full ${getSeverityBg(activity?.severity)}`}>
              <Icon 
                name={getActivityIcon(activity?.type)} 
                size={16} 
                className={getSeverityColor(activity?.severity)}
              />
            </div>
            
            <div className="flex-1 min-w-0">
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground">
                    <span className="font-semibold">{activity?.user}</span> {activity?.action}
                  </p>
                  <div className="flex items-center space-x-4 mt-1">
                    <span className="text-xs text-muted-foreground">
                      {formatTimestamp(activity?.timestamp)}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {activity?.ip}
                    </span>
                    <span className="text-xs text-muted-foreground">
                      {activity?.device}
                    </span>
                  </div>
                </div>
                <Icon 
                  name="ChevronRight" 
                  size={16} 
                  className="text-muted-foreground flex-shrink-0 ml-2"
                />
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

export default AuditActivityFeed;